import { BaseApiService } from './BaseApiService'
import { PricingResponseAPI, CountryRate, PricingData } from '@/types/pricing'
import { getCountryConfig } from '@/config/countries'
import { getCountryName } from '@/lib/countries'

/**
 * Pricing API Service
 *
 * Handles API calls related to pricing data,
 * including country rates and pricing information
 */
export class PricingApiService extends BaseApiService {
  private cachedPricingData: PricingData | null = null

  /**
   * Fetch pricing data from API and join with country config
   */
  async getPricing(): Promise<PricingData> {
    // Return cached data if available
    if (this.cachedPricingData) {
      return this.cachedPricingData
    }

    const result = await this.get<PricingResponseAPI>('/pricing')

    // Join pricing data with country config
    const countries = result.countries
      .map(country => {
        const config = getCountryConfig(country.countryCode)
        if (!config) {
          console.warn(`No country config found for code: ${country.countryCode}`)
          return null
        }

        return {
          countryCode: country.countryCode,
          country: getCountryName(country.countryCode),
          region: config.region,
          dialingCode: config.dialingCode,
          landlineRate: country.landlineRate / 100,
          mobileRate: country.mobileRate / 100,
          currency: country.currency ?? result.currency,
          popular: country.popular || false,
          dialingCodePreferred: config.dialingCodePreferred || false
        } as CountryRate
      })
      .filter((country): country is CountryRate => country !== null)

    const pricingData: PricingData = {
      countries,
      agentFeePerMin: result.agentFeePerMin ?? null,
      currency: result.currency,
      stats: {
        totalCountries: countries.length,
        minRate: Math.min(
            ...countries.map(c => c.landlineRate).filter(rate => rate > 0),
            ...countries.map(c => c.mobileRate).filter(rate => rate > 0)
        ),
        averageRate: countries.length > 0
          ? countries.reduce((acc, c) => acc + Math.min(c.landlineRate, c.mobileRate), 0) / countries.length
          : 0
      }
    }

    // Cache the processed data
    this.cachedPricingData = pricingData

    return pricingData
  }

  /**
   * Clear cached pricing data (useful for forcing refresh)
   */
  clearCache(): void {
    this.cachedPricingData = null
  }

  /**
   * Get pricing rate for a specific phone number
   * Validates if the number can be called and returns the rate per minute
   * @param phoneNumber - The phone number in E.164 format (e.g., +1234567890)
   * @returns Object containing validity status and rate information
   */
  async getRate(phoneNumber: string): Promise<{ price_per_min: number; currency: string }> {
    const encodedNumber = encodeURIComponent(phoneNumber)
    return this.get<{ price_per_min: number; currency: string }>(`/pricing/rate?phoneNumber=${encodedNumber}`, {
      requiresAuth: true,
    })
  }
}

// Singleton instance
export const pricingApiService = new PricingApiService()

// Export both the class and the instance for backward compatibility
export default pricingApiService

import { queryOptions } from '@tanstack/react-query'

export const pricingQueries = {
  all: ['pricing'] as const,

  pricing: () => queryOptions({
    queryKey: [...pricingQueries.all, 'data'] as const,
    queryFn: () => pricingApiService.getPricing(),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    refetchOnWindowFocus: false,
  }),

}
